import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Button } from "react-bootstrap";
import { notify as reapopNotify } from "reapop";
import TitleBar from "@common/components/TitleBar/TitleBar";
import Spinner from "@common/components/Spinner/Spinner";
import * as breweryActions from "@brewery/actions/breweryActions";
import mqttService from "@monitor/mqttService";
import NOTIFICATION_TYPES from "@common/constants/notificationTypes";
import BreweryModalProvider from "./context/BreweryModalProvider";
import { useBreweryModalContext } from "./hooks/useBreweryModalContext";
import { useBrewEligibility } from "./hooks/useBrewEligibility";
import { usePendingFermentation } from "./hooks/usePendingFermentation";
import BrewStatsBar from "./components/BrewStatsBar/BrewStatsBar";
import PendingFermentationAlert from "./components/PendingFermentationAlert/PendingFermentationAlert";
import DeviceSection from "./components/DeviceSection/DeviceSection";
import BreweryEmptyState from "./components/BreweryEmptyState/BreweryEmptyState";
import BreweryModals from "./components/BreweryModals/BreweryModals";
import "./brewery.scss";

const COMMAND_FAILED = 1;

export const Brewery = ({
  brewery,
  userInfo,
  fetchBrewery,
  notify,
  brewEligible: propBrewEligible,
  nextBrewDate: propNextBrewDate,
  brewStats: propBrewStats,
}) => {
  const { actions: modalActions } = useBreweryModalContext();

  // Async brew eligibility & brew stats queries
  const eligibility = useBrewEligibility();
  const brewEligible =
    propBrewEligible !== undefined
      ? propBrewEligible
      : eligibility.brewEligible;
  const nextBrewDate =
    propNextBrewDate !== undefined
      ? propNextBrewDate
      : eligibility.nextBrewDate;
  const brewStats =
    propBrewStats !== undefined ? propBrewStats : eligibility.brewStats;

  // Interrupted fermentation intent detection & recovery
  const {
    pendingFermentationDevice,
    resumePendingFermentation,
    dismissPendingFermentation,
  } = usePendingFermentation(brewery && brewery.list);

  // Initial fetch and live MQTT notifications
  useEffect(() => {
    if (fetchBrewery) {
      fetchBrewery();
    }

    const sub = mqttService.notification$.subscribe((notification) => {
      if (fetchBrewery) {
        fetchBrewery();
      }

      if (
        notification &&
        notification.type === NOTIFICATION_TYPES.NOT_ERROR &&
        notification.error === COMMAND_FAILED &&
        notify
      ) {
        notify({
          title: "",
          message: notification.message,
          status: "warning",
          dismissible: true,
          dismissAfter: 10000,
          position: "br",
        });
      }
    });

    return () => {
      if (sub && typeof sub.unsubscribe === "function") {
        sub.unsubscribe();
      }
    };
  }, [fetchBrewery, notify]);

  const breweryOverview = brewery && brewery.list;
  const noDevices =
    breweryOverview &&
    (!breweryOverview.brew_clean_idle ||
      breweryOverview.brew_clean_idle.length === 0) &&
    (!breweryOverview.brew_acid_clean_idle ||
      breweryOverview.brew_acid_clean_idle.length === 0) &&
    (!breweryOverview.fermenting || breweryOverview.fermenting.length === 0) &&
    (!breweryOverview.serving || breweryOverview.serving.length === 0);

  return (
    <div>
      <TitleBar title="BREWERY" subtitle="Overview">
        <Button
          onClick={modalActions.openAddDeviceModal}
          bsStyle="warning"
          style={{ position: "absolute", right: "15px" }}
        >
          ADD DEVICE
        </Button>
      </TitleBar>

      <div className="container-fluid">
        <div className="row">
          {breweryOverview && (
            <BrewStatsBar
              brewStats={brewStats}
              userInfo={userInfo}
              nextBrewDate={nextBrewDate}
            />
          )}

          {pendingFermentationDevice && (
            <PendingFermentationAlert
              device={pendingFermentationDevice}
              onResume={resumePendingFermentation}
              onDismiss={dismissPendingFermentation}
            />
          )}

          {breweryOverview && !noDevices && (
            <div className="col-md-12">
              <DeviceSection
                title="BREWING"
                devices={[
                  ...(breweryOverview.brew_clean_idle || []),
                  ...(breweryOverview.brew_acid_clean_idle || []),
                ]}
                lastUpdate={brewery?.lastUpdate}
                sortByDeviceType
                brewEligible={brewEligible}
                nextBrewDate={nextBrewDate}
              />

              {Boolean(breweryOverview.fermenting?.length) && (
                <DeviceSection
                  title="FERMENTING"
                  devices={breweryOverview.fermenting}
                  showDivider
                  brewEligible={brewEligible}
                  nextBrewDate={nextBrewDate}
                />
              )}

              {Boolean(breweryOverview.serving?.length) && (
                <DeviceSection
                  title="SERVING"
                  devices={breweryOverview.serving}
                  showDivider
                  brewEligible={brewEligible}
                  nextBrewDate={nextBrewDate}
                />
              )}
            </div>
          )}

          {!breweryOverview && <Spinner className={"center-spinner"} />}

          {breweryOverview && noDevices && (
            <div className="col-md-12">
              <BreweryEmptyState
                onAddDevice={modalActions.openAddDeviceModal}
              />
            </div>
          )}
        </div>
      </div>

      <BreweryModals userInfo={userInfo} fetchBrewery={fetchBrewery} />
    </div>
  );
};

Brewery.propTypes = {
  brewery: PropTypes.shape({
    list: PropTypes.object,
    lastUpdate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  userInfo: PropTypes.object,
  fetchBrewery: PropTypes.func,
  notify: PropTypes.func,
  brewEligible: PropTypes.bool,
  nextBrewDate: PropTypes.string,
  brewStats: PropTypes.object,
};

Brewery.defaultProps = {
  brewery: null,
  userInfo: null,
  fetchBrewery: () => {},
  notify: () => {},
  brewEligible: undefined,
  nextBrewDate: undefined,
  brewStats: undefined,
};

const mapStateToProps = ({ brewery, userInfo }) => ({
  brewery,
  userInfo,
});

const mapDispatchToProps = (dispatch) => ({
  fetchBrewery: () => dispatch(breweryActions.fetchBrewery()),
  notify: (data) => dispatch(reapopNotify(data)),
});

const ConnectedBrewery = connect(mapStateToProps, mapDispatchToProps)(Brewery);

export const BreweryPage = (props) => (
  <BreweryModalProvider>
    <ConnectedBrewery {...props} />
  </BreweryModalProvider>
);

export default BreweryPage;
